import React, { useState, useEffect, useId } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { faQuestionCircle, faEnvelope, faSearch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectUser } from '../../../redux/userSlice'
import { selectAuth } from '../../../redux/authSlice'
import { selectModules } from '../../../redux/moduleSlice'
import { setNotification } from '../../../redux/notificationSlice'
import { hasUserPermission } from '../../../utils/permissionHelper'
import LayoutSidebar from '../../../components/LayoutSidebar'
import Loading from '../../../components/Loading'
import TableHeader from '../../../components/TableHeader'
import TableRow from '../../../components/TableRow'
import Pagination from '../../../components/Pagination'
import { useRequest } from '../../../hooks/UseRequest'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { selectPortfolios } from "../../../redux/portfolioSlice"
import { buildQueryString } from '../../../helpers/formatting'

/**
 * Page for displaying leads that have been withdrawn and require adverse action
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export default function WithdrawnLeadsWithAdverseAction(props) {
  const params = useParams()
  const navi = useNavigate()
  const startId = useId()
  const dispatch = useDispatch()

  // Redux Selectors
  const modules = useSelector(selectModules)
  const active = useSelector(selectUser)
  const auth = useSelector(selectAuth)
  const requests = useRequest()
  const dropdownOptions = useSelector(selectDropdownOptions)
  const portfolios = useSelector(selectPortfolios)

  // Local State
  const [module, setModule] = useState()
  const [moduleCount, setModuleCount] = useState(-1)
  const [loaded, setLoaded] = useState(true)
  const [partialLoad, setPartialLoad] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')
  const [urlFilter, setUrlFilter] = useState('')
  const [userPermissions, setUserPermissions] = useState()
  const [moduleData, setModuleData] = useState([])
  const [moduleFields, setModuleFields] = useState()
  const [validPortfolios, setValidPortfolios] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getFirstDayOfPreviousMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1).toISOString().split('T')[0];
  };
  const getLastDayOfPreviousMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 0).toISOString().split('T')[0];
  };

  const { control, handleSubmit, setValue, formState: { errors }, watch, getValues } = useForm({
    defaultValues: {
      portfolio_id: null,
      start_date: getFirstDayOfPreviousMonth(),
      end_date: getLastDayOfPreviousMonth(),
    }
  })

  const portfolioId = watch('portfolio_id')?.value
  const startDate = watch('start_date')
  const endDate = watch('end_date')

  const displayFields = [
    'guid', // Lead #
    'customer_id', // Customer
    'withdrawn_at', // Withdrawn Date
    'lead_withdrawal_reason_dropdown_option_id', // Withdrawal Reason
    'aa_email_id', // AA Email
    'sent_date', // AA Email Sent Date
    'portfolio_id', // Portfolio #
    'created_at', // Created At
    'created_by', // Created By
    'updated_at', // Updated At
    'updated_by', // Updated By
  ]

  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = active.spoofed_user.user_permission_options
        ? active.spoofed_user.user_permission_options
        : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (!hasUserPermission(activePermissions, `${module.code_key}_read`)) {
        navi('/403')
      }
    }
  }, [active, module])

  useEffect(() => {
    if (active.data.limit_to_portfolio_id || active?.spoofed_user?.limit_to_portfolio_id) {
      let limitedPortfolioIds = active?.spoofed_user?.limit_to_portfolio_id ?? (active.data.limit_to_portfolio_id)
      let limitedPortfolio = []
      limitedPortfolioIds.forEach((portfolioId) => {
        let portfolioData = portfolios.data.filter((portfolio) => portfolio.id === portfolioId)
        portfolioData = portfolioData[0] ?? null
        if (portfolioData) {
          limitedPortfolio.push(portfolioData)
        }
      })
      setValidPortfolios(limitedPortfolio)
    } else {
      setValidPortfolios(portfolios.data)
    }
  }, [active, portfolios])

  /**
   * Ensure that the module being accessed actually exists
   */
  const calculateDisplayRank = (item, fields) => {
    return fields.indexOf(item)
  }

  useEffect(() => {
    const mw = modules.data.find((e) => e.code_key === 'lead_withdrawn');
    if (mw) {
      setModule(mw);

      const mf = [...mw.module_fields];
      // Filter and sort while keeping column_name unique
      const uniqueFilteredAndSorted = mf
        .filter((m) => {
          if (m.column_name !== 'id') {
            const rank = calculateDisplayRank(m.column_name, displayFields);
            return rank >= 0;
          }
        })
        .reduce((acc, current) => {
          const x = acc.find((item) => item.column_name === current.column_name);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, [])
        .sort((a, b) => calculateDisplayRank(a.column_name, displayFields) - calculateDisplayRank(b.column_name, displayFields));
      setModuleFields(uniqueFilteredAndSorted);
    } else {
      navi('/404');
    }
  }, [params.module_name]);

  const getWithdrawnLeads = async (url) => {
    try {
      setPartialLoad(false)
      requests.setEndpoint(url)
      const response = await requests.get()
      // Add a module_code_key to each item to identify it in the TableRow component
      response.data.data = response.data.data.map((item) => {
        return {
          ...item,
          module_code_key: 'withdrawn_leads'
        }
      });

      setModuleData(response.data)
      setPartialLoad(true)
      setIsSubmitting(false)
    } catch (error) {
      setPartialLoad(true)
      setIsSubmitting(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  // Function to get portfolio options based on NavBarDesktop selection
  const getPortfolioOptions = () => {
    if (!validPortfolios) return []
    // If NavBarDesktop has "All Portfolios" selected
    if (!active.filtered_portfolio) {
      // Show all valid portfolios
      return validPortfolios
        .filter((p) => p.id != null)
        .map((p) => ({ label: p.name, value: p.id }))
    }

    // If NavBarDesktop has a specific portfolio selected,
    // return only that portfolio as an option
    const selectedPortfolio = validPortfolios.find((p) => p.id === active.filtered_portfolio)
    return selectedPortfolio ? [{ label: selectedPortfolio.name, value: selectedPortfolio.id }] : []
  }

  // Update the useEffect to handle portfolio synchronization
  useEffect(() => {
    if (auth.accessToken && validPortfolios.length > 0) {
      let url = `${process.env.REACT_APP_API_DOMAIN}/withdrawn_leads`
      let paramData = {};
      if (active.filtered_portfolio) {
        // If NavBarDesktop has a specific portfolio selected
        const selectedPortfolio = validPortfolios.find((p) => p.id === active.filtered_portfolio)
        if (selectedPortfolio) {
          // Force the withdrawn list to use the NavBarDesktop selected portfolio
          setValue('portfolio_id', {
            label: selectedPortfolio.name,
            value: selectedPortfolio.id
          })

          paramData = {
            portfolio_id: selectedPortfolio.id,
            withdrawn_date_start: startDate ? `${startDate}` : null,
            withdrawn_date_end: endDate ? `${endDate}` : null
          }
        }
      } else {
        // clear selection when all portfolios selected.
        setValue('portfolio_id', null)
        paramData = {
          portfolio_id: null,
          withdrawn_date_start: startDate ? `${startDate}` : null,
          withdrawn_date_end: endDate ? `${endDate}` : null
        }
      }

      url += buildQueryString(paramData);
      getWithdrawnLeads(url)
    }
  }, [auth.accessToken, validPortfolios, active.filtered_portfolio])

  // Update the onSubmit function
  const onSubmit = (data) => {
    if (auth.accessToken) {
      setIsSubmitting(true)
      let url = `${process.env.REACT_APP_API_DOMAIN}/withdrawn_leads`

      // Use NavBarDesktop portfolio if selected, otherwise use form selection
      const portfolioId = active.filtered_portfolio || data.portfolio_id?.value

      const paramData = {
        portfolio_id: portfolioId,
        withdrawn_date_start: data.start_date ? `${data.start_date} 00:00:00` : null,
        withdrawn_date_end: data.end_date ? `${data.end_date} 23:59:59` : null
      }
      url += buildQueryString(paramData);
      getWithdrawnLeads(url)
    }
  }

  /**
   * After the API call for module data succeeds stop loading and set the record count
   */
  useEffect(() => {
    if (moduleData && moduleData?.meta?.total) {
      setModuleCount(moduleData.meta.total)
    } else {
      setModuleCount(moduleData.data && moduleData.data.length)
    }
  }, [moduleData])

  if (module) {
    const count = 0

    const contentTitle = (
      <>
        <span>
          Leads: Withdrawn Requiring Adverse Action ({moduleCount > 0 ? moduleCount : 0})
        </span>
        {module.helper_text && (
          <span className="lox-no-border ml-1 lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary" data-tooltip={module.helper_text}>
            <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
          </span>
        )}
      </>
    )

    return (
      <LayoutSidebar
        contentTitle={contentTitle}
        content={loaded || partialLoad ? (
          <div className="p-5">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="has-pagination pt-3 mb-5">
                <div className="field is-horizontal mb-4">
                  <div className="is-flex" style={{ width: '100%', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Portfolio Filter */}
                    <div className="is-flex" style={{ alignItems: 'center' }}>
                      <label htmlFor="portfolio_id" style={{ width: '100px', marginBottom: 0 }}>Portfolio:</label>
                      <div style={{ width: '300px' }}>
                        <Controller
                          name="portfolio_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              isSearchable={false}
                              classNames={reactSelectClassNames()}
                              options={getPortfolioOptions()} // Only show allowed options
                              value={active.filtered_portfolio ?
                                // If NavBarDesktop has a specific portfolio, force that selection
                                getPortfolioOptions()[0] :
                                // Otherwise use the field value
                                field.value}
                              onChange={(val) => {
                                // Only allow changes when "All Portfolios" is selected in NavBarDesktop
                                if (!active.filtered_portfolio) {
                                  field.onChange(val)
                                }
                              }}
                              menuPosition="fixed"
                              styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                              // Only allow clearing when "All Portfolios" is selected
                              isClearable={!active.filtered_portfolio}
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Date Range Filters */}
                    <div className="is-flex" style={{ alignItems: 'center' }}>
                      <label style={{ width: '180px', marginBottom: 0 }}>Withdraw Start Date:</label>
                      <div style={{ width: '200px' }}>
                        <Controller
                          name="start_date"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              className="input is-small"
                              type="date"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="is-flex" style={{ alignItems: 'center' }}>
                      <label style={{ width: '180px', marginBottom: 0 }}>Withdraw End Date:</label>
                      <div style={{ width: '200px' }}>
                        <Controller
                          name="end_date"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!startDate || !value) return true;
                              return new Date(value) >= new Date(startDate) || "End date must be after start date";
                            }
                          }}
                          render={({ field }) => (
                            <>
                              <input
                                {...field}
                                className="input is-small"
                                type="date"
                              />
                              {errors.end_date && (
                                <p className="has-text-danger is-size-7 mt-1">{errors.end_date.message}</p>
                              )}
                            </>
                          )}
                        />
                      </div>
                    </div>

                    {/* Search Button */}
                    <div className="control">
                      <button
                        type="submit"
                        className="button is-primary is-small"
                        disabled={isSubmitting}
                      >
                        <span className="icon is-small">
                          <FontAwesomeIcon icon={faSearch} />
                        </span>
                        <span>{isSubmitting ? 'Searching...' : 'Search'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <div className={`${moduleData.data?.length <= 3 && 'lox-hooktable-dataless'} lox-hooktable table-container mb-0 position-relative`}>
              {!partialLoad && loaded && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              <div className="list-table-container">
                <table className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button">
                  <TableHeader
                    columns={moduleFields}
                    startId={startId}
                    count={count}
                    hasActions={false}
                    hasView
                    setPartialLoad={setPartialLoad}
                    url={module.code_key + urlFilter}
                    searchFilter={searchFilter}
                    module={module}
                  />
                  {moduleData?.data?.length === 0 && (
                    <tbody>
                      <tr>
                        <td className="is-size-6 is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }} colSpan={displayFields.length + 1}>No Record(s) Found</td>
                      </tr>
                    </tbody>
                  )}
                  <tbody className={partialLoad && !loaded ? 'loading' : ''}>
                    <TableRow
                      rows={moduleData.data}
                      columns={moduleFields}
                      hasActions={false}
                      baseName={module.code_key}
                      setPartialLoad={setPartialLoad}
                      customRowName={'withdrawn_leads'}
                    />
                  </tbody>
                </table>
              </div>
            </div>
            {moduleData && moduleData?.meta?.last_page > 1 && (
              <Pagination
                meta={moduleData.meta}
                setPartialLoad={setPartialLoad}
                paginateCallback={getWithdrawnLeads}
              />
            )}
          </div>
        ) : <Loading />}
      />
    )
  }
}
